import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import {
  registerPlugin,
  UndoRedo,
  ManualColumnMove,
  ManualColumnResize,
  ManualRowMove,
  CopyPaste,
  AutoColumnSize,
  ContextMenu
} from 'handsontable/plugins';
import { RootState } from 'redux/store';
import {
  addDataGridSeriesAction,
  deleteDataGridSeriesAction,
  transposeDataAction,
  moveCellsAction,
  remapAction
} from 'pages/ChartEditorPage/actions/chartEditor';
import {
  cellsConfig,
  editData,
  hotContextMenuConfig,
  afterGetColHeader,
  beforeOnCellMouseDown,
  CoordProps,
  setDataMap
} from 'shared/wizard/utils/dataGridHelper';
import { updateGridData } from 'redux/reducers/dataGridReducer';
import useCategoriesAndSeries from '../utils/useCategoriesAndSeries';
import { UnknownParamsFunction } from 'shared/types/commonPropTypes';
import IconButton from 'shared/buttons/IconButton';
import { cloneDeep } from 'lodash';
import { getLetterFromIndex, parseCSV } from 'editor/core/highcharts-editor';
import { parsePastedValue } from '../utils/dragAndDropHelper';
import classNames from 'classnames';
import { getAllowProps, getIsLiveDataSource } from '../utils/seriesHelper';
import { ButtonColor } from 'shared/buttons/models/ButtonModels';

registerPlugin(UndoRedo);
registerPlugin(ManualColumnResize);
registerPlugin(CopyPaste);
registerPlugin(AutoColumnSize);
registerPlugin(ContextMenu);
registerPlugin(ManualColumnMove);
registerPlugin(ManualRowMove);

type DataGridProps = {
  isTable?: boolean;
  togglePasting?: (toggle?: boolean) => void;
  minRows?: number;
};

const MIN_COLS = 4;

export default function DataGrid(props: DataGridProps) {
  const { togglePasting } = props;
  const dispatch = useDispatch();
  const hotTableRef = useRef<HotTable | null>({} as HotTable);
  const { seriesAssigns, dataOptions, activeColumns, aggregatedOptions, dataConfig, isPastingActive } = useSelector(
    (state: RootState) => state.projectConfig
  );
  const { type } = useSelector((state: RootState) => state.layoutEditorPage);
  const { mappedData, isMap } = useSelector((state: RootState) => state.chartEditorPage);
  const oldColHeaders = useSelector((state: RootState) => state.dataGridConfig.colHeaders);
  const [parsedData, setParsedData] = useState<Handsontable.RowObject[]>(
    cloneDeep(dataOptions.length ? dataOptions : [[]])
  );

  const { seriesTypes } = getAllowProps(aggregatedOptions, props.isTable);
  const getIndexForAssignRawOptions = seriesTypes.includes('mappoint') ? 1 : 0;
  const isPointMap = isMap && seriesTypes.includes('mappoint');
  const { categoriesIndexes, seriesIndexes } = useCategoriesAndSeries(getIndexForAssignRawOptions);
  const isLiveDataSource = getIsLiveDataSource(aggregatedOptions, dataConfig);

  useEffect(() => {
    document.querySelector('.everviz-datagrid')?.addEventListener('wheel', function (event) {
      if ((event as WheelEvent).deltaX < 0) event.preventDefault();
    });
  }, []);

  const setData = (options: unknown, dataType: string, cb?: UnknownParamsFunction | null, skipDataHelper?: boolean) => {
    dispatch(setDataMap[type]({ options, dataType, cb, skipDataHelper }));
  };

  const parseMapValues = (data?: Handsontable.RowObject[]) =>
    setParsedData(
      (data ?? parsedData).map((item: Handsontable.RowObject) => {
        item[0] = mappedData[item[0]] ?? item[0];
        return item;
      })
    );

  useEffect(() => {
    if (isMap) parseMapValues(cloneDeep(dataOptions));
    else setParsedData(cloneDeep(dataOptions));

    const headers: never[] = dataOptions[0];
    if (headers.length !== oldColHeaders.length) {
      const gridData = hotTableRef.current?.hotInstance?.getData() as [[]];
      const colHeaders = hotTableRef.current?.hotInstance?.getColHeader();
      if (gridData && colHeaders) {
        let newColHeaders = headers.map((_, index) => getLetterFromIndex(index));
        if (newColHeaders.length < MIN_COLS) {
          newColHeaders = Array.from({ length: MIN_COLS }, (_, i) => newColHeaders[i] ?? getLetterFromIndex(i));
        }
        dispatch(updateGridData({ gridData, colHeaders: newColHeaders }));
      }
    }
  }, [dataOptions]);

  useEffect(() => {
    if (isMap) parseMapValues();
  }, [mappedData]);

  const onBeforeChange = (changes: Handsontable.CellChange[]) => {
    if (isPastingActive && togglePasting) togglePasting(false);
    dispatch(editData[type]({ changes, seriesAssigns, isTable: props.isTable }));
  };

  const onCreate = (index: number, amount: number, source?: Handsontable.ChangeSource) => {
    if (
      source === 'ContextMenu.rowAbove' ||
      source === 'ContextMenu.rowBelow' ||
      source === 'ContextMenu.columnLeft' ||
      source === 'ContextMenu.columnRight'
    ) {
      dispatch(addDataGridSeriesAction({ index, amount, source }));
    }
  };

  const onColumnMove = (movedColumns: number[], finalIndex: number) => {
    if (movedColumns.length && movedColumns[0] === finalIndex) return;
    dispatch(moveCellsAction({ type, moveType: 'columns', movedCells: movedColumns, finalIndex }));
    return false;
  };

  const onRowMove = (movedRows: number[], finalIndex: number) => {
    dispatch(moveCellsAction({ type, moveType: 'row', movedCells: movedRows, finalIndex }));
    return false;
  };

  const onRemove = (index: number, amount: number, physicalCells: number[], source?: Handsontable.ChangeSource) => {
    if (source === 'ContextMenu.removeRow' || source === 'ContextMenu.removeColumn') {
      dispatch(deleteDataGridSeriesAction({ index, amount, source }));
    }
  };

  const transposeData = () => {
    if (isLiveDataSource) return;
    dispatch(
      transposeDataAction({
        type,
        callback: (data: Handsontable.RowObject[]) => {
          setParsedData(data);
        }
      })
    );
  };

  const onBeforeOnCellMouseDown = (e: MouseEvent, coords: CoordProps) => {
    beforeOnCellMouseDown(e, coords, hotTableRef.current?.hotInstance as Handsontable);
  };

  const beforePaste = (
    value: unknown[][],
    coords: { endRow: number; endCol: number; startCol: number; startRow: number }[]
  ) => {
    const parsedValue = parsePastedValue(window.event as ClipboardEvent, props.isTable);
    const data = parseCSV(parsedValue, null, null);

    Object.assign(value, data, { length: data.length });

    // Replace empty strings with a null value, Highcharts doesnt like null values in its data
    value.forEach((row: unknown[], rowIndex: number) => {
      row.forEach((cell: unknown, columnIndex: number) => {
        if (cell === '') value[rowIndex][columnIndex] = null;
      });
    });

    const hot = hotTableRef.current?.hotInstance;
    const selectedAllCells =
      coords?.length === 1 &&
      coords[0].startCol === 0 &&
      coords[0].startRow === 0 &&
      hot?.countCols() === coords[0].endCol + 1 &&
      hot?.countRows() === coords[0].endRow + 1;

    // Selected all cells and trying to paste. Do a normal import in this case
    if ((selectedAllCells || isPastingActive) && togglePasting) {
      setData(value, 'csv', () => togglePasting(false), isMap);
      return false;
    }
  };

  const afterPaste = () => {
    dispatch(
      remapAction({
        type,
        skipNotification: !isPastingActive,
        cb: () => {
          if (isPastingActive && togglePasting) togglePasting();
        }
      })
    );
  };

  const getCells = (row: number, col: number) => {
    if (isLiveDataSource) return {};
    return cellsConfig(row, col, isMap, categoriesIndexes, seriesIndexes, isPointMap, (activeColumns ?? {}).columns);
  };

  const tableClass = classNames({
    'live-data': isLiveDataSource,
    'everviz-datatable': true
  });
  const containerClass = classNames('relative', {
    'pasting-active': isPastingActive
  });

  return (
    <div className={containerClass}>
      <HotTable
        ref={hotTableRef}
        data={parsedData}
        cells={getCells}
        rowHeaders={true}
        colHeaders={true}
        manualColumnMove={true}
        manualRowMove={true}
        contextMenu={hotContextMenuConfig}
        copyPaste={true}
        manualColumnResize={true}
        minRows={props.minRows ?? 20}
        minCols={MIN_COLS}
        stretchH="all"
        // @ts-expect-error ts-2339
        licenseKey={window.hcconfig.dataGridLicenceKey ?? 'non-commercial-and-evaluation'}
        className={tableClass}
        beforeChange={onBeforeChange}
        beforeCreateRow={onCreate}
        beforeCreateCol={onCreate}
        beforeRemoveRow={onRemove}
        beforeRemoveCol={onRemove}
        afterGetColHeader={afterGetColHeader}
        beforeOnCellMouseDown={onBeforeOnCellMouseDown}
        beforeColumnMove={onColumnMove}
        beforeRowMove={onRowMove}
        beforePaste={beforePaste}
        afterPaste={afterPaste}
        bindRowsWithHeaders={true}
      />
      {!isMap && (
        <div className="switch-rows absolute top-[0px] left-[0px] w-[50px] h-[30px] z-[1200]">
          <IconButton
            icon={isLiveDataSource ? 'lock' : 'repeat'}
            className="w-full h-full  mx-0"
            buttonColor={ButtonColor.Transparent}
            onClick={transposeData}
          />
        </div>
      )}
      {isLiveDataSource && (
        <div className="w-full h-[531px] bg-transparent absolute top-0 z-[1300]">
          <div className="absolute top-[77px] left-[85px] z-[3000] italic font-bold text-ev-grey-2 bg-white">
            Unable to show preview of live data
          </div>
        </div>
      )}
    </div>
  );
}
